import * as React from 'react'
import * as Lodash from 'lodash'
import update from 'immutability-helper'

import { FormatsetComponent } from './Formatset'

import { arrayToObject } from '../../utils/array'

import { Project } from '../../interfaces/entities/project'
import { Formatset, FormatsetId } from '../../interfaces/entities/formatset'
import { Creative, CreativeAction } from '../../interfaces/entities/creative'
import { User } from '../../interfaces/entities/user'

import CollapseButton from '../CollapseButton'

import { Divider, Typography } from '@design/pt-ui'
import { translateWithUserLocale } from '../../utils/i18n'
import { CreativesFilter } from '../../pages/ProjectView/hooks'
import { Platform } from '../../interfaces/entities/platform'

interface IFormatsetProps {
  project: Project
  user: User
  creativesFilter: CreativesFilter['state']
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  onRemoveFormatSet: (formatsetId: FormatsetId) => any
  moveFormatset?: (
    dragIndex: number,
    hoverIndex: number,
    platform: string,
  ) => void
  platforms: Platform[]
}

export interface State {
  sortableFormatsets: Array<{
    id: string
    platform: string
  }>
}

function useFormatsetCollapsing(formatsets: Formatset[]) {
  const ids = formatsets
    .map((f) => f.id)
    .sort()
    .join('_')

  const initialState = React.useMemo(() => {
    return arrayToObject(formatsets, (formatset) => {
      return [formatset.id, false]
    })
  }, [ids]) // eslint-disable-line react-hooks/exhaustive-deps

  const [collapsed, setCollapsed] = React.useState(initialState)

  React.useEffect(() => {
    setCollapsed(initialState)
  }, [ids]) // eslint-disable-line react-hooks/exhaustive-deps

  const setCollapsedForOneFormatset = React.useCallback(
    (collapsed: boolean, id: string) => {
      setCollapsed((state) => {
        return { ...state, [id]: collapsed }
      })
    },
    [],
  )

  const collapseAll = React.useCallback(() => {
    setCollapsed((state) => {
      return Lodash.mapValues(state, () => true)
    })
  }, [])

  const uncollapseAll = React.useCallback(() => {
    setCollapsed((state) => {
      return Lodash.mapValues(state, () => false)
    })
  }, [])

  const allCollapsed = React.useMemo(
    () => Lodash.every(collapsed, (v, k) => v === true),
    [collapsed],
  )
  const someCollapsed = React.useMemo(
    () => Lodash.some(collapsed, (v, k) => v === true),
    [collapsed],
  )

  const toggleAllCollapsed = React.useCallback(() => {
    if (someCollapsed) {
      return uncollapseAll()
    } else {
      return collapseAll()
    }
  }, [someCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    someCollapsed,
    allCollapsed,
    collapsed,
    collapseAll,
    uncollapseAll,
    toggleAllCollapsed,
    setCollapsedForOneFormatset,
    setCollapsed,
  }
}

const filterCreativesFromFormatset = (
  formatset: Formatset,
  filter: CreativesFilter['state'],
) => {
  const placements = filter.placements.map((o) => o.value)
  let satisfiesPlacements = true

  placements.forEach((placement) => {
    const elements = placement.split('__$__')
    const path = elements.slice(0, -1).join('.')

    const value = elements.length > 0 ? elements[elements.length - 1] : null
    const values = Lodash.get(formatset.data.placement, path) || []

    if (!values.includes(value)) {
      satisfiesPlacements = false
    }
  })

  if (!satisfiesPlacements) {
    return { ...formatset, creatives: [] }
  }

  const name = filter.query
  const states = filter.states.map((o) => o.value)
  const types = filter.formats.map((o) => o.value)

  const filteredCreatives = formatset.creatives.filter((creative) => {
    if (states.length > 0 && !states.includes(creative.status.name)) {
      return false
    }

    if (types.length > 0 && !types.includes(creative.type.name)) {
      return false
    }

    if (!creative.name.toUpperCase().includes(name.toUpperCase())) {
      return false
    }

    return true
  })

  return {
    ...formatset,
    creatives: filteredCreatives,
  }
}

function FormatsetsComponent(props: IFormatsetProps) {
  const { project } = props
  const formatsets = project.formatsets

  const [state, setState] = React.useState<State>({
    sortableFormatsets: props.project.formatsets.map((formatset) => {
      return { id: formatset.id, platform: formatset.type }
    }),
  })

  const { sortableFormatsets } = state

  const formatsetsSorted = props.project.formatsets
    .map((f) => f.id)
    .sort()
    .join('_')

  React.useEffect(() => {
    setState((state) => ({
      ...state,
      sortableFormatsets: props.project.formatsets.map((formatset) => {
        return { id: formatset.id, platform: formatset.type }
      }),
    }))
  }, [formatsetsSorted]) // eslint-disable-line react-hooks/exhaustive-deps

  const moveFormatset = (dragIndex: any, hoverIndex: any, platform: string) => {
    const dragFormatset = sortableFormatsets[dragIndex]
    if (dragFormatset.platform === platform) {
      setState(
        update(state, {
          sortableFormatsets: {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, dragFormatset],
            ],
          },
        }),
      )
    }
  }

  const {
    allCollapsed,
    toggleAllCollapsed,
    setCollapsedForOneFormatset,
    collapsed,
  } = useFormatsetCollapsing(formatsets)

  const filterFormatSets = (
    sortableFormatsets: { id: string; platform: string }[],
    creativesFilter: IFormatsetProps['creativesFilter'],
    creativesFilterEmpty: boolean,
  ) => {
    let filteredFormatSets = []

    for (const sortableFormatset of sortableFormatsets) {
      const formatset = props.project.formatsets.find(
        (f) => f.id === sortableFormatset.id,
      )
      if (formatset) {
        const filteredFormatset = filterCreativesFromFormatset(
          formatset,
          creativesFilter,
        )
        if (filteredFormatset.creatives.length > 0 || creativesFilterEmpty) {
          filteredFormatSets.push({
            ...filteredFormatset,
            platform: sortableFormatset.platform,
          })
        }
      }
    }

    const sortedFilteredFs = [
      ...filteredFormatSets.filter((fs) => fs.type === 'facebook'),
      ...filteredFormatSets.filter(
        (fs) => fs.type === 'performance_advertising',
      ),
    ]

    return sortedFilteredFs
  }

  const renderFormat = (filteredFormatset: any, index: number) => {
    return (
      <React.Fragment key={index}>
        <FormatsetComponent
          {...props}
          formatset={filteredFormatset}
          id={filteredFormatset.id}
          index={index}
          key={index}
          moveFormatset={moveFormatset}
          collapsed={collapsed[filteredFormatset.id]}
          setCollapsed={(collapsed: boolean) => {
            setCollapsedForOneFormatset(collapsed, filteredFormatset.id)
          }}
        />
        {formatsets.length > index + 1 ? (
          <Divider sx={{ margin: '40px 0' }} />
        ) : (
          ''
        )}
      </React.Fragment>
    )
  }
  const platformsForView =
    props.creativesFilter.platforms.length === 0
      ? props.platforms
      : props.creativesFilter.platforms

  const creativesFilterEmpty =
    props.creativesFilter.formats.length === 0 &&
    props.creativesFilter.placements.length === 0 &&
    props.creativesFilter.query === '' &&
    props.creativesFilter.states.length === 0

  const filteredFormatsets = filterFormatSets(
    sortableFormatsets,
    props.creativesFilter,
    creativesFilterEmpty,
  )
  return (
    <div>
      <span
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: '2px',
        }}>
        <Typography variant="body2">
          {translateWithUserLocale('labels.collapse_expand_all')}
        </Typography>
        <CollapseButton collapsed={allCollapsed} onClick={toggleAllCollapsed} />
      </span>
      {platformsForView.map((creativePlatform) => (
        <div key={creativePlatform.value}>
          {filteredFormatsets.filter(
            (sf) => sf.platform === creativePlatform.value,
          ).length > 0 && (
            <h2 style={{ paddingBottom: 40 }}>{creativePlatform.label}</h2>
          )}
          {filteredFormatsets
            .filter((sf) => sf.platform === creativePlatform.value)
            .map((filteredFormatset, i) =>
              renderFormat(
                filteredFormatset,
                filteredFormatsets.indexOf(filteredFormatset),
              ),
            )}
        </div>
      ))}
    </div>
  )
}

export default FormatsetsComponent
